import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  changeCityStatus,
  createCity,
  deleteCity,
  getCities,
  updateCity,
} from '../api/cityApi'
import type { City, CityStatus, CityUpsert, PagedResponse } from '../types'

const KEY = 'cities'

/**
 * Seçili ülkenin şehir yönetimi sunucu-durumu (026). Sorgu `countryId`'ye göre anahtarlanır ve yalnız bir
 * ülke seçiliyken etkindir (`enabled`); ülke değişince liste otomatik yenilenir. Mutation'lar da `countryId`
 * taşır (nested uç). (023 useEventTypes deseninin ülke-kapsamlı ikizi.)
 */
export function useCities(countryId: number | null) {
  const { i18n } = useTranslation()
  const qc = useQueryClient()
  const scopeKey = [KEY, countryId, i18n.language] as const

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [KEY, countryId] })
  }

  const applyUpdated = (updated: City) =>
    qc.setQueryData<PagedResponse<City>>(scopeKey, (prev) =>
      prev
        ? { ...prev, content: prev.content.map((c) => (c.id === updated.id ? updated : c)) }
        : prev,
    )

  const list = useQuery({
    queryKey: scopeKey,
    queryFn: () => getCities(countryId as number),
    enabled: countryId != null,
  })

  const create = useMutation({
    mutationFn: (body: CityUpsert) => createCity(countryId as number, body),
    onSuccess: invalidate,
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: CityUpsert }) =>
      updateCity(countryId as number, id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })

  const changeStatus = useMutation({
    mutationFn: ({ id, body }: { id: number; body: CityStatus }) =>
      changeCityStatus(countryId as number, id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
  })

  const remove = useMutation({
    mutationFn: (id: number) => deleteCity(countryId as number, id),
    onSuccess: invalidate,
  })

  return { list, create, update, changeStatus, remove }
}
